import { localeCompareAlphanumeric } from '@/utils/base'
import moment from 'moment'
import { lengthGroupSorter } from '@/components/account/account-scaling/length-groups/LengthGroups.js'
import { diameterGroupSorter } from '@/components/account/account-scaling/diameter-groups/DiameterGroups.js'
import { cloneDeep } from 'lodash'

export const VersionedRateSheetStatus = {
  Expired: {
    id: 0,
    name: 'expired',
    color: 'secondary',
    icon: 'mdi-cancel'
  },
  Active: {
    id: 1,
    name: 'active',
    color: 'success',
    icon: 'mdi-checkbox-marked-circle'
  },
  Inactive: {
    id: 2,
    name: 'inactive',
    color: 'secondary',
    icon: 'mdi-circle-outline'
  }
}

class RateSheetDefinition {
  constructor (name, accountId, rates) {
    this.name = name
    this.accountId = accountId
    this.rates = rates
  }
}
export class RateSheetDefinitionRequest extends RateSheetDefinition {
  constructor (name, accountId, rates = []) {
    const ratesMapped = rates.map((r) => new VersionedRateSheetRequest(r.effectiveDate, r.rateSheetDefinitionLines))
    super(name, accountId, ratesMapped)
  }
}
export class RateSheetDefinitionBuilder extends RateSheetDefinition {
  constructor (name = null, account = null, rates = []) {
    const ratesMapped = rates.map((r) => new VersionedRateSheetBuilder(r.effectiveDate, r.rateSheetDefinitionLines))
    super(name, account?.accountId, ratesMapped)
    this.account = account
  }

  get request () {
    return new RateSheetDefinitionRequest(
      this.name,
      this.account?.accountId,
      this.rates.map((r) => r.request)
    )
  }
}

class VersionedRateSheet {
  constructor (effectiveDate, rateSheetDefinitionLines) {
    this.effectiveDate = effectiveDate
    this.rateSheetDefinitionLines = rateSheetDefinitionLines
  }
}
export class VersionedRateSheetRequest extends VersionedRateSheet {
  constructor (effectiveDate, rateSheetDefinitionLines = []) {
    super(effectiveDate, rateSheetDefinitionLines)
  }
}
export class VersionedRateSheetBuilder extends VersionedRateSheet {
  constructor (effectiveDate = null, rateSheetDefinitionLines = []) {
    const rateSheetDefinitionLinesMapped =
      rateSheetDefinitionLines.map((l) => new RateSheetDefinitionLineBuilder(l.rateSheetDefinitionLineId, l.rate, l.speciesGroup, l.lengthGroup, l.diameterGroup, l.grade, l.endUseSort))
    super(effectiveDate, rateSheetDefinitionLinesMapped)
  }

  get request () {
    return new VersionedRateSheetRequest(
      this.effectiveDate,
      this.rateSheetDefinitionLines
    )
  }
}

export class GenericRateSheetLine {
  constructor (rate, speciesGroup, lengthGroup, diameterGroup, grade, endUseSort) {
    this.rate = rate
    this.speciesGroup = speciesGroup
    this.lengthGroup = lengthGroup
    this.diameterGroup = diameterGroup
    this.grade = grade
    this.endUseSort = endUseSort

    this.rate = rate
    this.speciesGroupId = speciesGroup?.speciesGroupId
    this.lengthGroupId = lengthGroup?.lengthGroupId
    this.diameterGroupId = diameterGroup?.diameterGroupId
    this.gradeId = grade?.gradeId
    this.endUseSortId = endUseSort?.endUseSortId

    this.genericGetFormattedForTable = () => {
      return {
        rate: this.rate,
        speciesGroup: this.speciesGroup?.label,
        lengthGroup: this.lengthGroup?.label,
        diameterGroup: this.diameterGroup?.label,
        grade: this.grade?.name,
        endUseSort: this.endUseSort?.name
      }
    }

    this.hasSpeciesGroupByLabel = (label) => {
      return this.speciesGroup?.label === label
    }

    this.hasSpeciesGroupById = (id) => {
      return this.speciesGroup?.speciesGroupId === id
    }

    this.hasNoSpeciesGroup = () => {
      return !this.speciesGroup
    }
  }
}
export class RateSheetDefinitionLineBuilder extends GenericRateSheetLine {
  constructor (rateSheetDefinitionLineId = null, rate = null, speciesGroup = null, lengthGroup = null, diameterGroup = null, grade = null, endUseSort = null) {
    super(rate, speciesGroup, lengthGroup, diameterGroup, grade, endUseSort)
    this.rateSheetDefinitionLineId = rateSheetDefinitionLineId
  }
}

export function rateSheetDefinitionLineFromServerToBuilder (line) {
  const lineClone = cloneDeep(line)
  return new RateSheetDefinitionLineBuilder(
    lineClone.rateSheetDefinitionLineId,
    lineClone.rate,
    lineClone.speciesGroupId ? {
      speciesGroupId: lineClone.speciesGroupId,
      label: lineClone.speciesGroupLabel
    } : null,
    lineClone.lengthGroupId ? {
      lengthGroupId: lineClone.lengthGroupId,
      label: lineClone.lengthGroupLabel
    } : null,
    lineClone.diameterGroupId ? {
      diameterGroupId: lineClone.diameterGroupId,
      label: lineClone.diameterGroupLabel
    } : null,
    lineClone.gradeId ? {
      gradeId: lineClone.gradeId,
      name: lineClone.gradeName
    } : null,
    lineClone.endUseSortId ? {
      endUseSortId: lineClone.endUseSortId,
      name: lineClone.endUseSortName
    } : null
  )
}

export function rateSheetDefinitionSorter ({ name: a }, { name: b }) {
  return localeCompareAlphanumeric(a, b)
}

export function rateSheetDefinitionVersionSorter (vsnA, vsnB) {
  return moment(vsnA.effectiveDate).isBefore(vsnB.effectiveDate) ? -1 : 1
}

export function rateSheetDefinitionLineSorter (lineA, lineB) {
  let result = 0
  result = localeCompareAlphanumeric(lineA.speciesGroup?.label ?? '', lineB.speciesGroup?.label ?? '')
  if (result !== 0) return result

  if (lineA.lengthGroup && lineB.lengthGroup) result = lengthGroupSorter(lineA.lengthGroup, lineB.lengthGroup)
  if (result !== 0) return result

  if (lineA.diameterGroup && lineB.diameterGroup) result = diameterGroupSorter(lineA.diameterGroup, lineB.diameterGroup)
  if (result !== 0) return result

  result = localeCompareAlphanumeric(lineA.grade?.name ?? '', lineB.grade?.name ?? '')
  if (result !== 0) return result

  result = localeCompareAlphanumeric(lineA.endUseSort?.name ?? '', lineB.endUseSort?.name ?? '')
  if (result !== 0) return result
  return 0
}

export function assignVersionedRateSheetStatus (versionedRateSheetDefns) {
  if (!(versionedRateSheetDefns?.length > 0)) {
    return
  }
  versionedRateSheetDefns.sort((a, b) => moment(a.effectiveDate).diff(moment(b.effectiveDate)))
  const effectiveBeforeToday = versionedRateSheetDefns.filter(beforeTodayFilter)
  const effectiveFuture = versionedRateSheetDefns.filter(effectiveFutureFilter)
  let currentVsn
  if (effectiveBeforeToday.length > 0) currentVsn = effectiveBeforeToday.pop()

  effectiveBeforeToday.forEach(pastVrsd => { pastVrsd.status = VersionedRateSheetStatus.Expired })
  effectiveFuture.forEach(futureVrsd => { futureVrsd.status = VersionedRateSheetStatus.Inactive })
  if (currentVsn) currentVsn.status = VersionedRateSheetStatus.Active
}

export function getActiveRateSheetVersion (versionedRateSheetDefns) {
  if (!(versionedRateSheetDefns?.length > 0)) {
    return
  }
  versionedRateSheetDefns.sort((a, b) => moment(a.effectiveDate).diff(moment(b.effectiveDate)))
  const effectiveBeforeToday = versionedRateSheetDefns.filter(beforeTodayFilter)
  return effectiveBeforeToday.pop()
}

function beforeTodayFilter (versionedRateSheetDefn) {
  return moment(versionedRateSheetDefn.effectiveDate).isSameOrBefore(moment().endOf('day'))
}

function effectiveFutureFilter (versionedRateSheetDefn) {
  return moment(versionedRateSheetDefn.effectiveDate).isAfter(moment().endOf('day'))
}
